import { PropertyCode } from 'src/modules/shared/components/atoms/property-code';
import { PropertySharingAction } from 'src/modules/shared/components/molecules/property-sharing-action';
import { getPropertyClassificationLabel } from '../../utils/property-classification.util';
import { PropertyAssigneeTagDropdown } from '../molecules/property-assignee-tag-dropdown';
import { useHasPermissions } from 'src/modules/shared/hooks/auth';
import { useCurrentUser } from 'src/modules/shared/hooks/api/admin/users';
import { PropertyPermission } from 'src/modules/shared/types/permissions.type';
import type { User } from 'src/api/generated';
import React from 'react';
import { Table } from 'src/modules/shared/components/molecules/table/table';
import { type TableColumnProps } from 'src/modules/shared/components/molecules/table/table';
import { type CatalogProperty, PropertyOperation } from 'src/api/generated';
import { Text } from 'src/modules/shared/components/atoms/text';
import { formatCurrency } from 'src/modules/shared/utils/formatting/currency.util';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';
import {
  TagDropdown,
  type TagDropdownOption,
} from 'src/modules/shared/components/molecules/dropdowns/tag-dropdown';
import {
  PiDotsThreeVertical,
  PiPauseCircle,
  PiPencilSimple,
  PiEye,
} from 'react-icons/pi';
import type { TranslationKey } from 'src/modules/shared/types/i18n/translation-keys.type';

const propertyOperationLabelKeyByOperation: Record<
  PropertyOperation,
  TranslationKey
> = {
  [PropertyOperation.SALE]: 'catalog.properties.operation.SALE',
  [PropertyOperation.RENT]: 'catalog.properties.operation.RENT',
  [PropertyOperation.SALE_AND_RENT]:
    'catalog.properties.operation.SALE_AND_RENT',
  [PropertyOperation.TEMPORARY_RENT]:
    'catalog.properties.operation.TEMPORARY_RENT',
};

export interface PropertiesTableProps {
  properties: CatalogProperty[];
  loading: boolean;
  isLoadingMore?: boolean;
  users: User[];
  usersLoading: boolean;
  isAssigning?: boolean;
  onUserAssign: (propertyId: number, userId: number | null) => void;
  onEdit?: (property: CatalogProperty) => void;
  onDeactivate?: (property: CatalogProperty) => void;
}

export const PropertiesTable: React.FC<PropertiesTableProps> = ({
  properties,
  loading,
  isLoadingMore = false,
  users,
  usersLoading,
  isAssigning = false,
  onUserAssign,
  onEdit,
  onDeactivate,
}) => {
  const { t } = useI18n();
  const { hasPermission: canReadAll } = useHasPermissions(
    PropertyPermission.Read
  );
  const { hasPermission: canReadScoped } = useHasPermissions(
    PropertyPermission.ReadScoped
  );
  const { hasPermission: canUpdateAll } = useHasPermissions(
    PropertyPermission.Update
  );
  const { hasPermission: canUpdateScoped } = useHasPermissions(
    PropertyPermission.UpdateScoped
  );
  const { user } = useCurrentUser();

  const formatPrice = (amount?: string | null, currency?: string | null) => {
    if (!amount || !currency) {
      return '—';
    }

    return formatCurrency(amount, currency, 0);
  };

  const columns: TableColumnProps<CatalogProperty>[] = [
    {
      header: t('catalog.properties.table.codigo'),
      accessor: 'id',
      className: 'whitespace-nowrap',
      render: (_value, property) => (
        <Text size="small" className="whitespace-nowrap">
          <PropertyCode
            displayCode={property.displayCode}
            fallback={String(property.id)}
            internalPrefix=""
          />
        </Text>
      ),
    },
    {
      header: t('catalog.properties.table.name'),
      accessor: 'name',
      render: (_value, property) => (
        <Text size="small" weight="medium">
          {property.name || '—'}
        </Text>
      ),
    },
    {
      header: t('catalog.properties.table.cadastralId'),
      accessor: 'cadastralId',
      render: (_value, property) => (
        <Text size="tiny" color="secondary">
          {property.cadastralId ?? '—'}
        </Text>
      ),
    },
    {
      header: t('catalog.properties.table.operation'),
      accessor: 'operation',
      render: (_value, property) => {
        const operation = property.operation ?? undefined;

        if (!operation) {
          return (
            <Text size="tiny" color="secondary">
              {'—'}
            </Text>
          );
        }

        const label = t(propertyOperationLabelKeyByOperation[operation]);

        return (
          <Text size="tiny" color="secondary">
            {label}
          </Text>
        );
      },
    },
    {
      header: t('catalog.properties.table.assigned'),
      accessor: 'userId',
      render: (_value, property) => {
        return (
          <PropertyAssigneeTagDropdown
            property={property}
            users={users}
            usersById={new Map(users.map((user) => [user.id, user]))}
            usersLoading={usersLoading}
            isAssigning={isAssigning}
            onUserAssign={onUserAssign}
            t={t}
          />
        );
      },
    },
    {
      header: t('catalog.properties.table.price'),
      accessor: 'salePriceAmount',
      render: (_value, property) => {
        const hasSale =
          !!property.salePriceAmount && !!property.salePriceCurrency;
        const hasRent =
          !!property.rentPriceAmount && !!property.rentPriceCurrency;

        if (!hasSale && !hasRent) {
          return (
            <Text size="tiny" color="secondary">
              {'—'}
            </Text>
          );
        }

        if (hasSale && !hasRent) {
          return (
            <Text size="tiny" color="secondary">
              {formatPrice(
                property.salePriceAmount,
                property.salePriceCurrency
              )}
            </Text>
          );
        }

        if (!hasSale && hasRent) {
          return (
            <Text size="tiny" color="secondary">
              {formatPrice(
                property.rentPriceAmount,
                property.rentPriceCurrency
              )}
            </Text>
          );
        }

        return (
          <div className="flex flex-col gap-0.5">
            <Text size="tiny" color="secondary">
              {t('catalog.properties.price.saleLabel')}:{' '}
              {formatPrice(
                property.salePriceAmount,
                property.salePriceCurrency
              )}
            </Text>
            <Text size="tiny" color="secondary">
              {t('catalog.properties.price.rentLabel')}:{' '}
              {formatPrice(
                property.rentPriceAmount,
                property.rentPriceCurrency
              )}
            </Text>
          </div>
        );
      },
    },
    {
      header: t('catalog.properties.table.commonExpenses'),
      accessor: 'commonExpensesAmount',
      render: (_value, property) => (
        <Text size="tiny" color="secondary">
          {formatPrice(
            property.commonExpensesAmount,
            property.commonExpensesCurrency
          )}
        </Text>
      ),
    },
    {
      header: t('catalog.properties.table.propertyType'),
      accessor: 'propertyType',
      render: (_value, property) => {
        const type = property.propertyType ?? undefined;

        if (!type) {
          return (
            <Text size="tiny" color="secondary">
              {'—'}
            </Text>
          );
        }

        const label = getPropertyClassificationLabel(
          type,
          property.propertySubtype,
          t
        );

        return (
          <Text size="tiny" color="secondary">
            {label}
          </Text>
        );
      },
    },
    {
      header: t('catalog.properties.table.address'),
      accessor: 'addressDisplay',
      render: (_value, property) => {
        const addressDisplay = property.addressDisplay ?? '';
        const addressParts = [
          property.streetName ?? '',
          property.streetNumber ?? '',
          property.unit ?? '',
        ].filter((part) => part.trim().length > 0);

        const fallbackAddress =
          addressParts.length > 0 ? addressParts.join(' ') : '';

        const value = addressDisplay || fallbackAddress;

        return (
          <Text size="tiny" color="secondary">
            {value || '—'}
          </Text>
        );
      },
    },
    {
      header: t('catalog.properties.table.updated'),
      accessor: 'updatedAt',
      render: (_value, property) => {
        const updated = new Date(property.updatedAt);
        return (
          <Text size="tiny" color="secondary">
            {updated.toLocaleDateString()}
          </Text>
        );
      },
    },
    {
      header: '',
      accessor: 'actions',
      render: (_value, property) => {
        const inScope = user != null && property.userId === user.id;
        const canUpdate = canUpdateAll || (canUpdateScoped && inScope);
        const canRead = canReadAll || (canReadScoped && inScope);
        if (!canRead && !canUpdate) return null;
        const options: TagDropdownOption[] = [
          {
            id: canUpdate ? 'edit' : 'view',
            content: (
              <Text size="small" color="secondary">
                {t(
                  canUpdate
                    ? 'catalog.properties.table.actions.edit'
                    : 'catalog.properties.table.actions.view'
                )}
              </Text>
            ),
            icon: canUpdate ? (
              <PiPencilSimple className="w-4 h-4 text-secondary-700" />
            ) : (
              <PiEye className="w-4 h-4 text-secondary-700" />
            ),
          },
          ...(canUpdate && property.isActive
            ? [
                {
                  id: 'deactivate',
                  content: (
                    <Text size="small" color="danger">
                      {t('catalog.properties.table.actions.deactivate')}
                    </Text>
                  ),
                  icon: <PiPauseCircle className="w-4 h-4 text-danger-700" />,
                } satisfies TagDropdownOption,
              ]
            : []),
        ];

        return (
          <div className="flex items-center gap-2">
            <PropertySharingAction sharingLink={property.sharingLink} />
            <TagDropdown
              content={
                <div className="flex items-center">
                  <PiDotsThreeVertical className="w-4 h-4" />
                </div>
              }
              variant="primary"
              appearance="soft"
              size="sm"
              placement="right-start"
              options={options}
              onOptionSelect={(option) => {
                if ((option.id === 'edit' || option.id === 'view') && onEdit) {
                  onEdit(property);
                }
                if (option.id === 'deactivate' && onDeactivate) {
                  onDeactivate(property);
                }
              }}
            />
          </div>
        );
      },
    },
  ];

  return (
    <Table<CatalogProperty>
      variant="neutral"
      columns={columns}
      data={properties}
      keyExtractor={(property) => property.id.toString()}
      isLoading={loading && properties.length === 0}
      isLoadingMore={isLoadingMore}
      emptyStateMessage={t('catalog.properties.table.emptyState.title')}
      emptyStateDescription={t(
        'catalog.properties.table.emptyState.description'
      )}
    />
  );
};
